import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import usStates from '../../us-states.json';

function AdminLeads({ token }) {
  const [allLeads, setAllLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [allAgents, setAllAgents] = useState([]);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [agentNames, setAgentNames] = useState({});

  useEffect(() => {
    fetchLeads();
    fetchAgents();
  }, []);

 useEffect(() => {
    applyFilters();
  }, [searchTerm, stateFilter, agentFilter, startDate, endDate, allLeads]);

  useEffect(() => {
    // Reset to page 1 when filters change
    setPage(1);
  }, [searchTerm, stateFilter, agentFilter, startDate, endDate]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/admin/distribution/leads?limit=10000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const leads = response.data.leads || [];
      setAllLeads(leads);
      setFilteredLeads(leads);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leads:', error);
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      // Use summary endpoint - much faster (no carriers/states data)
      const response = await axios.get('/api/admin/agents/summary', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const agents = response.data || [];
      setAllAgents(agents);
      
      // Create email -> name mapping
      const nameMapping = {};
      agents.forEach(agent => {
        nameMapping[agent.email] = agent.name;
      });
      setAgentNames(nameMapping);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const applyFilters = () => {
    let filtered = [...allLeads];

    // Search term filter (searches lead email, phone, and agent email)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(lead => 
        (lead.leadEmail || lead.email || '').toLowerCase().includes(term) ||
        (lead.leadPhone || '').toLowerCase().includes(term) ||
        (lead.agentEmail || '').toLowerCase().includes(term)
      );
    }

    // State filter
    if (stateFilter) {
      filtered = filtered.filter(lead => 
        (lead.state || '').toUpperCase() === stateFilter
      );
    }

    // Agent email filter (multiple selection)
    if (agentFilter.length > 0) {
      filtered = filtered.filter(lead =>
        agentFilter.includes(lead.agentEmail)
      );
    }

    // Date range filter - convert lead timestamp to US Eastern date
    if (startDate) {
      filtered = filtered.filter(lead => {
        const leadDate = new Date(lead.timestamp);
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/Chicago',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
        const parts = formatter.formatToParts(leadDate);
        const year = parts.find(p => p.type === 'year').value;
        const month = parts.find(p => p.type === 'month').value;
        const day = parts.find(p => p.type === 'day').value;
        const leadDateEastern = `${year}-${month}-${day}`;
        return leadDateEastern >= startDate;
      });
    }

    if (endDate) {
      filtered = filtered.filter(lead => {
        const leadDate = new Date(lead.timestamp);
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: 'America/Chicago',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        });
        const parts = formatter.formatToParts(leadDate);
        const year = parts.find(p => p.type === 'year').value;
        const month = parts.find(p => p.type === 'month').value;
        const day = parts.find(p => p.type === 'day').value;
        const leadDateEastern = `${year}-${month}-${day}`;
        return leadDateEastern <= endDate;
      });
    }

    setFilteredLeads(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStateFilter('');
    setAgentFilter([]);
    setStartDate('');
    setEndDate('');
  };

  const filterByToday = () => {
    // Get today's date in US Eastern timezone
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(now);
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    const todayEastern = `${year}-${month}-${day}`;
    
    setStartDate(todayEastern);
    setEndDate(todayEastern);
  };

  const filterByYesterday = () => {
    // Get yesterday's date in US Eastern timezone
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const parts = formatter.formatToParts(yesterday);
    const year = parts.find(p => p.type === 'year').value;
    const month = parts.find(p => p.type === 'month').value;
    const day = parts.find(p => p.type === 'day').value;
    const yesterdayEastern = `${year}-${month}-${day}`;
    
    setStartDate(yesterdayEastern);
    setEndDate(yesterdayEastern);
  };

  const handleAgentFilterChange = (email) => {
    setAgentFilter(prev => {
      if (prev.includes(email)) {
        return prev.filter(e => e !== email);
      } else {
        return [...prev, email];
      }
    });
  };

  // Pagination
  const totalPages = Math.ceil(filteredLeads.length / limit);
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedLeads = filteredLeads.slice(startIndex, endIndex);

  // Calculate unique lead count (by email OR phone)
  const uniqueLeadCount = new Set(
    filteredLeads.map(lead => lead.leadEmail || lead.leadPhone || lead.email).filter(Boolean)
  ).size;

  if (loading && allLeads.length === 0) return <div className="loading">Loading Opportunities...</div>;

  return (
    <div className="container">
      <h2>Opportunities Distribution ({uniqueLeadCount})</h2>

      {/* Filters */}
      <div style={{ 
        background: '#f8f9fa', 
        padding: '20px', 
        borderRadius: '8px', 
        marginTop: '24px'
      }}>
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Search (Email/Phone)
            </label>
            <input
              type="text"
              placeholder="Search email or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
              State
            </label>
            <select
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: 'white'
              }}
            >
              <option value="">All States</option>
              {usStates.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name} ({state.code})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Agent Filter - Multi-select Autocomplete */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
            Filter by Agents {agentFilter.length > 0 && <span style={{ color: '#667eea' }}>({agentFilter.length} selected)</span>}
          </label>
          <Select
            isMulti
            options={allAgents.map(agent => ({
              value: agent.email,
              label: `${agent.name} (${agent.email})`
            }))}
            value={agentFilter.map(email => {
              const agent = allAgents.find(a => a.email === email);
              return {
                value: email,
                label: agent ? `${agent.name} (${agent.email})` : email
              };
            })}
            onChange={(selected) => {
              setAgentFilter(selected ? selected.map(s => s.value) : []);
            }}
            placeholder="Search and select agents..."
            isClearable
            isSearchable
            menuPortalTarget={document.body}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }),
              control: (base) => ({
                ...base,
                minHeight: '42px'
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#667eea',
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: 'white',
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: 'white',
                ':hover': {
                  backgroundColor: '#5568d3',
                  color: 'white',
                },
              }),
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={filterByToday}
            className="btn btn-primary btn-sm"
          >
            📅 Today (US Eastern)
          </button>
          <button
            onClick={filterByYesterday}
            className="btn btn-primary btn-sm"
          >
            📅 Yesterday (US Eastern)
          </button>
          <button
            onClick={clearFilters}
            className="btn btn-secondary btn-sm"
          >
            Clear All Filters
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ marginTop: '24px', overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Opportunity Contact</th>
              <th>Agent</th>
              <th>State</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {paginatedLeads.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: 'center', padding: '40px' }}>
                  {allLeads.length === 0 
                    ? 'No Opportunities distributed yet' 
                    : 'No Opportunities match your filters'}
                </td>
              </tr>
            ) : (
              paginatedLeads.map((lead, idx) => {
                // Display email if available, otherwise phone, otherwise '-'
                const leadContact = lead.leadEmail || lead.email || lead.leadPhone || '-';
                const contactType = lead.leadEmail || lead.email ? '📧' : (lead.leadPhone ? '📱' : '');
                
                return (
                <tr key={idx}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {contactType && <span style={{ fontSize: '14px' }}>{contactType}</span>}
                      <span>{leadContact}</span>
                    </div>
                  </td>
                  <td>
                    {/* ⭐ SHOW GROUP INFO IF GROUP LEAD */}
                    {lead.isGroupLead && lead.memberEmail ? (
                      <div>
                        {/* Compact: GROUP badge + Group Name */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}>
                          <span style={{
                            padding: '2px 6px',
                            background: 'linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)',
                            color: 'white',
                            borderRadius: '3px',
                            fontSize: '9px',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px'
                          }}>
                            👥 GROUP
                          </span>
                          <span style={{ fontWeight: '600', fontSize: '13px' }}>
                            {lead.agentName || agentNames[lead.agentEmail] || 'Unknown Group'}
                          </span>
                        </div>
                        {/* Member email only */}
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {lead.memberEmail || '-'}
                        </div>
                      </div>
                    ) : (
                      /* Regular individual agent */
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                          {lead.agentName || agentNames[lead.agentEmail] || 'Unknown'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {lead.agentEmail || '-'}
                        </div>
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="badge">{lead.state || '-'}</span>
                  </td>
                  <td>{lead.timestamp ? new Date(lead.timestamp).toLocaleString('en-US', {
                    timeZone: 'America/Chicago',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                  }) : '-'}</td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredLeads.length > 0 && (
        <div className="pagination" style={{ marginTop: '24px', display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center' }}>
          {totalPages > 1 && (
            <button 
              className="btn btn-sm btn-secondary"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
          )}
          <span style={{ padding: '8px 16px', fontSize: '14px', color: '#6b7280' }}>
            Showing {startIndex + 1}-{Math.min(endIndex, filteredLeads.length)} of {filteredLeads.length} leads
            {totalPages > 1 && ` (Page ${page} of ${totalPages})`}
          </span>
          {totalPages > 1 && (
            <button 
              className="btn btn-sm btn-secondary"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminLeads;
